/* Renders non-blocking warnings under an input, from a data-warnings attribute holding a JSON
 * array of messages:
 *
 *   ["This makes your nutrition info based on 10,000 grams."]
 *
 * Errors block the save and use a tooltip (see field_with_errors.js); warnings stay visible
 * under the field so they can be read while the value is still being edited.
 *
 * The message lands in the input's .field. When that field is too narrow for a sentence --
 * a quantity next to its unit, say -- wrap the row in [data-field-warning-anchor] and the
 * message goes under the whole row instead. Inputs sharing an anchor share one block holding
 * every field's messages, deduplicated. */

const anchorFor = (input) =>
  input.closest("[data-field-warning-anchor]") ?? input.closest(".field");

const messagesFor = (input) => {
  if (!input.dataset.warnings) return [];

  return JSON.parse(input.dataset.warnings);
};

const buildMessage = (message) => {
  const line = document.createElement("p");
  line.className = "help has-text-warning-dark icon-text-inline";

  const icon = document.createElement("span");
  icon.className = "icon";

  const glyph = document.createElement("i");
  glyph.className = "fas fa-exclamation-triangle mr-1";

  const text = document.createElement("span");
  text.textContent = message;

  icon.appendChild(glyph);
  line.append(icon, text);

  return line;
};

/* Fields sharing an anchor share the block under it, and a rule spanning them (Saturated and
 * Trans fat, say) warns on each, so the same sentence is only worth one line. */
const anchorMessages = (anchor) => [
  ...new Set(
    Array.from(anchor.querySelectorAll("[data-warnings]"))
      .filter((input) => anchorFor(input) === anchor)
      .flatMap(messagesFor)
  ),
];

const processInput = (input) => {
  const anchor = anchorFor(input);
  if (!anchor) return;

  anchor.querySelector(":scope > [data-field-warning]")?.remove();

  const messages = anchorMessages(anchor);
  if (messages.length === 0) return;

  const warnings = document.createElement("div");
  warnings.dataset.fieldWarning = "";
  messages.forEach((message) => warnings.appendChild(buildMessage(message)));

  anchor.appendChild(warnings);
};

const scan = (container) => {
  container.querySelectorAll("[data-warnings]").forEach(processInput);
};

export const setFieldWarnings = (input, messages) => {
  if (!input) return;

  input.dataset.warnings = JSON.stringify(messages);
};

document.addEventListener("DOMContentLoaded", () => {
  scan(document);

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => processInput(mutation.target));
  }).observe(document.body, {
    attributes: true,
    attributeFilter: ["data-warnings"],
    subtree: true,
  });
});

document.addEventListener("partial:replace", (ev) => scan(ev.container));
